import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCan } from "@/lib/perms";
import { EmptyState, Loader } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { ORDER_TABS } from "@/lib/constants";
import { InvoiceSection } from "@/components/InvoiceSection";
import { CreditNoteSection } from "@/components/CreditNoteSection";
import { DuesSummary, PaymentBadge } from "@/components/OrderPayments";
import { ClipboardList, Plus, X, Trash2, Clock, ArrowRight, Check, AlertTriangle, FileText } from "lucide-react";

let _rowSeq = 0;
const newRow = () => ({ _key: `row-${Date.now()}-${_rowSeq++}`, product_id: "", product_name: "", unit: "", quantity: "" });

export default function Orders() {
  const { business } = useAuth();
  const can = useCan();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 20;
  const [tab, setTab] = useState("All");
  const [payment, setPayment] = useState("all");
  const [duesKey, setDuesKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [create, setCreate] = useState(false);
  const [reorderInit, setReorderInit] = useState(null);
  const isRestaurant = true;

  const load = useCallback(async () => {
    const r = await api.get("/orders", { params: { status: tab === "All" ? undefined : tab, payment: payment === "all" ? undefined : payment, page, per_page: PER_PAGE } });
    setOrders(r.data.orders); setTotal(r.data.total ?? r.data.orders.length); setLoading(false); setDuesKey((k) => k + 1);
  }, [tab, page, payment]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isRestaurant) return;
    if (location.state?.reorder) { setReorderInit(location.state.reorder); setCreate(true); }
    else if (location.state?.placeOrder) { setCreate(true); }
  }, [location.state, isRestaurant]);

  const filtered = orders;

  if (loading) return <Loader />;

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Orders</h1>
          <p className="mt-1 text-stone-500">{isRestaurant ? "Create and track your supply requests." : "Manage incoming supply requests."}</p>
        </div>
        {isRestaurant && can("procurement.create") && <Button data-testid="create-order-btn" onClick={() => setCreate(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> New Supply Request</Button>}
      </div>

      <DuesSummary payment={payment} onPayment={(p) => { setPayment(p); setPage(1); }} refreshKey={duesKey} />

      <div className="flex gap-2 overflow-x-auto pb-1 rv-scroll">
        {ORDER_TABS.map((t) => (
          <button key={t} data-testid={`order-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => { setTab(t); setPage(1); }}
            className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === t ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-white text-stone-600 hover:border-[#84A98C]"}`}>
            {t}
          </button>
        ))}
      </div>

      {total > PER_PAGE && (
        <div className="flex items-center justify-between" data-testid="orders-pagination">
          <span className="text-sm text-stone-500">Page {page} of {Math.ceil(total / PER_PAGE)} · {total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev-page">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / PER_PAGE)} onClick={() => setPage((p) => p + 1)} data-testid="orders-next-page">Next</Button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No orders here" description="Orders matching this filter will appear here." testid="orders-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-left text-xs uppercase tracking-wider text-stone-400">
              <tr>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Partner</th>
                <th className="hidden px-5 py-3 sm:table-cell">Items</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0ECE1]">
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-[#FAF8F5]" data-testid={`order-row-${o.order_code}`}>
                  <td className="px-5 py-3 font-mono font-semibold text-stone-800">
                    <span className="inline-flex items-center gap-1.5">{o.order_code}
                      {o.invoice && <FileText className="h-4 w-4 text-[#C85A32]" aria-label="Invoice attached" data-testid={`order-invoice-icon-${o.order_code}`} />}
                      {o.emergency && <span className="rounded-full bg-[#FDF6F2] px-2 py-0.5 font-sans text-[10px] font-bold uppercase text-[#C85A32]" data-testid={`order-emergency-badge-${o.order_code}`}>Emergency</span>}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-stone-700">{o.direction === "outgoing" ? o.to_name : o.from_name}</td>
                  <td className="hidden px-5 py-3 text-stone-500 sm:table-cell">{o.items.length} item(s)</td>
                  <td className="px-5 py-3"><div className="flex flex-wrap items-center gap-1.5"><StatusBadge status={o.status} /><PaymentBadge status={o.payment_status} /></div></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {isRestaurant && o.direction === "outgoing" && can("procurement.create") && (
                        <button className="text-sm font-medium text-[#C85A32] hover:underline" onClick={() => { setReorderInit({ partner_business_id: o.to_business_id, items: o.items }); setCreate(true); }} data-testid={`order-reorder-${o.order_code}`}>Reorder</button>
                      )}
                      <button className="inline-flex items-center gap-1 text-sm font-medium text-[#0D5C3A] hover:underline" onClick={() => setDetail(o)} data-testid={`order-view-${o.order_code}`}>
                        View <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderDetail key={detail?.id || "none"} order={detail} onClose={() => { setDetail(null); load(); }} onChanged={load}
        onRefresh={(o) => setDetail(o)}
        onFollowUp={(init) => { setReorderInit(init); setCreate(true); }} />
      {isRestaurant && <CreateOrderModal open={create} onOpenChange={(o) => { setCreate(o); if (!o) setReorderInit(null); }} onSaved={load} initial={reorderInit} />}
    </div>
  );
}

function ItemsTable({ items, total }) {
  const priced = items.some((it) => it.unit_price != null);
  return (
    <div className="rounded-xl border border-[#E5E0D8] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#FAF8F5] text-left text-xs uppercase text-stone-400"><tr>
          <th className="px-4 py-2">Product</th><th className="px-4 py-2">Qty</th><th className="px-4 py-2">Unit</th>
          {priced && <th className="px-4 py-2 text-right">Unit Price</th>}
          {priced && <th className="px-4 py-2 text-right">Line Total</th>}
        </tr></thead>
        <tbody className="divide-y divide-[#F0ECE1]">
          {items.map((it, i) => (
            <tr key={`${it.product_name}-${it.unit}-${i}`}>
              <td className="px-4 py-2 text-stone-700">{it.product_name}</td>
              <td className="px-4 py-2">{it.quantity}</td>
              <td className="px-4 py-2 text-stone-500">{it.unit}</td>
              {priced && <td className="px-4 py-2 text-right text-stone-600">{it.unit_price != null ? `₹${Number(it.unit_price).toFixed(2)}` : "—"}</td>}
              {priced && <td className="px-4 py-2 text-right font-medium text-stone-800">{it.line_total != null ? `₹${Number(it.line_total).toFixed(2)}` : "—"}</td>}
            </tr>
          ))}
        </tbody>
        {priced && total != null && (
          <tfoot><tr className="border-t border-[#E5E0D8] bg-[#FAF8F5]">
            <td className="px-4 py-2 font-semibold text-stone-700" colSpan={4}>Order Total</td>
            <td className="px-4 py-2 text-right font-bold text-[#0D5C3A]" data-testid="order-total-amount">₹{Number(total).toFixed(2)}</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

function OrderDetail({ order, onClose, onChanged, onFollowUp, onRefresh }) {
  const { business } = useAuth();
  const can = useCan();
  const [busy, setBusy] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [mItems, setMItems] = useState([]);
  const [mNote, setMNote] = useState("");
  const [mFulfillment, setMFulfillment] = useState("deliver");
  const [misOpen, setMisOpen] = useState(false);
  const [short, setShort] = useState([]);
  const [followUp, setFollowUp] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [prices, setPrices] = useState([]);
  useEffect(() => {
    if (order && order.direction === "incoming") api.get("/products").then((r) => setCatalog(r.data.products)).catch(() => {});
  }, [order]);
  if (!order) return null;
  const isVendor = order.direction === "incoming";
  const catPrice = (name, unit) => {
    const p = catalog.find((x) => x.name.trim().toLowerCase() === name.trim().toLowerCase() && x.unit === unit);
    return p && p.price != null ? p.price : "";
  };
  const startAccept = () => {
    setPrices(order.items.map((it) => ({ product_name: it.product_name, unit: it.unit, quantity: it.quantity, unit_price: String(catPrice(it.product_name, it.unit) ?? "") })));
    setAccepting(true);
  };
  const setPrice = (i, v) => setPrices((p) => p.map((x, idx) => (idx === i ? { ...x, unit_price: v } : x)));
  const submitAccept = async () => {
    for (const p of prices) { const n = parseFloat(p.unit_price); if (p.unit_price === "" || isNaN(n) || n < 0) { toast.error(`Enter a valid price for ${p.product_name}`); return; } }
    await call("accept", { prices: prices.map((p) => ({ product_name: p.product_name, unit: p.unit, unit_price: parseFloat(p.unit_price) })) });
  };

  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/${path}`, body || {});
      const d = data?.stock_deductions || [];
      if (d.length) toast.success(`Delivered. Stock reduced: ${d.map((x) => `${x.product} −${x.quantity} ${x.unit} (left ${x.remaining})`).join(", ")}`);
      else toast.success("Done");
      onChanged(); onClose();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const startModify = () => { setMItems(order.items.map((it) => ({ ...it }))); setMNote(""); setMFulfillment(order.fulfillment_type || "deliver"); setModifying(true); };
  const setMItem = (i, patch) => setMItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const mItemTotal = (it) => { const p = parseFloat(catPrice(it.product_name, it.unit)); const q = parseFloat(it.quantity); return p > 0 && q > 0 ? +(p * q).toFixed(2) : 0; };
  const mOrderTotal = mItems.reduce((s, it) => s + mItemTotal(it), 0);
  const submitModify = async () => {
    if (!mNote.trim()) { toast.error("Please add a message explaining the change"); return; }
    for (const it of mItems) {
      if (!(parseFloat(it.quantity) >= 0)) { toast.error(`Enter a valid quantity for ${it.product_name}`); return; }
      if (catPrice(it.product_name, it.unit) === "") { toast.error(`Set a catalog price for ${it.product_name} in Products first`); return; }
    }
    const items = mItems.map((it) => ({ product_name: it.product_name, unit: it.unit, quantity: parseFloat(it.quantity) || 0 }));
    await call("modify", { items, note: mNote, fulfillment_type: mFulfillment });
  };
  const startMis = () => { setShort(order.items.map((it) => ({ ...it, checked: false, less: "" }))); setMisOpen(true); };
  const submitMis = async () => {
    const sh = short.filter((x) => x.checked && parseFloat(x.less) > 0).map((x) => ({ product_name: x.product_name, unit: x.unit, short_quantity: parseFloat(x.less) }));
    if (!sh.length) { toast.error("Select at least one product and enter the shortfall quantity"); return; }
    if (short.some((x) => x.checked && parseFloat(x.less) > parseFloat(x.quantity))) { toast.error("Shortfall can't be more than the ordered quantity"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/status`, { status: "Missed Delivery", shortages: sh });
      toast.success("Mis-delivery recorded");
      onChanged();
      setMisOpen(false);
      setFollowUp(sh);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const createFollowUp = () => {
    onFollowUp({
      partner_business_id: order.to_business_id,
      items: followUp.map((s) => ({ product_name: s.product_name, unit: s.unit, quantity: s.short_quantity })),
      date: order.requested_delivery_date || "",
      time: order.requested_delivery_time || "",
      notes: `Follow-up for shortfall on ${order.order_code}`,
      parent_order_id: order.id,
    });
    onClose();
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-lg grid-cols-1 overflow-y-auto overflow-x-hidden rv-scroll [&>*]:min-w-0" data-testid="order-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">{order.order_code}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-400">{order.direction === "outgoing" ? "To Supplier" : "From Buyer"}</p>
              <p className="font-semibold text-stone-800">{order.direction === "outgoing" ? order.to_name : order.from_name}</p>
              <p className="font-mono text-xs text-stone-500">{order.direction === "outgoing" ? order.to_code : order.from_code}</p>
            </div>
            <StatusBadge status={order.status} />
          </div>

          <ItemsTable items={order.items} total={order.order_total} />

          {order.status === "Missed Delivery" && order.billed_total != null && (
            <div className="flex items-center justify-between rounded-lg bg-rose-50 px-3 py-2 text-sm" data-testid="order-billed-amount">
              <span className="font-medium text-rose-700">Billed (received quantities only)</span>
              <span className="font-bold text-rose-700">₹{Number(order.billed_total).toFixed(2)}</span>
            </div>
          )}

          <div className="space-y-1 text-sm" data-testid="order-fulfillment-info">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#F0ECE1] px-2.5 py-0.5 text-xs font-semibold text-stone-700">{order.fulfillment_type === "pickup" ? "Self-Pickup" : "Delivery"}</span>
              {order.requested_delivery_date && <span className="text-stone-600"><b className="text-stone-400">{order.fulfillment_type === "pickup" ? "Pickup:" : "Delivery:"}</b> {order.requested_delivery_date}{order.requested_delivery_time ? ` at ${order.requested_delivery_time}` : ""}</span>}
            </div>
            {order.fulfillment_type !== "pickup" && order.delivery_address && <p className="text-stone-600"><b className="text-stone-400">Address:</b> {order.delivery_address}</p>}
          </div>
          {order.notes && <p className="rounded-lg bg-[#FAF8F5] px-3 py-2 text-sm text-stone-600"><b>Notes:</b> {order.notes}</p>}

          {order.modification && order.status === "Modified" && (
            <div className="rounded-xl border-2 border-[#D97706]/40 bg-amber-50 p-4" data-testid="order-modification-block">
              <p className="mb-2 text-sm font-semibold text-amber-800">Supplier proposed these changes</p>
              <ItemsTable items={order.modification.items} total={order.modification.order_total} />
              {order.modification.fulfillment_type && order.modification.fulfillment_type !== order.fulfillment_type && (
                <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-amber-800" data-testid="modification-fulfillment">Fulfillment changed to: <b>{order.modification.fulfillment_type === "pickup" ? "Self-Pickup (delivery unavailable)" : "Delivery"}</b></p>
              )}
              <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm text-stone-700"><b>Supplier's message:</b> {order.modification.note}</p>
              {!isVendor && can("procurement.approve") && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" disabled={busy} onClick={() => call("modification/accept")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="order-accept-modification-btn"><Check className="mr-1 h-4 w-4" /> Accept Changes</Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => call("modification/reject")} className="text-rose-600" data-testid="order-reject-modification-btn"><X className="mr-1 h-4 w-4" /> Reject</Button>
                </div>
              )}
              {isVendor && <p className="mt-2 text-xs text-stone-500">Waiting for the buyer to accept or reject.</p>}
            </div>
          )}

          {isVendor && order.stock_deductions?.length > 0 && (
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600" data-testid="order-stock-deductions">
              <p className="font-medium text-stone-800">Stock reduced on delivery</p>
              {order.stock_deductions.map((x) => <p key={x.product + x.unit}>{x.product}: −{x.quantity} {x.unit} · {x.remaining} {x.unit} left</p>)}
            </div>
          )}


          <InvoiceSection order={order} isSupplier={isVendor} canUpload={can("orders.approve", "orders.edit", "logistics.edit")} canPayment={can("orders.approve", "orders.edit")}
            onChanged={() => { api.get(`/orders/${order.id}`).then((r) => onRefresh(r.data.order)).catch(() => {}); onChanged(); }} />

          <CreditNoteSection order={order} isSupplier={isVendor} canUpload={can("orders.approve", "orders.edit", "finance.view")}
            onChanged={() => { api.get(`/orders/${order.id}`).then((r) => onRefresh(r.data.order)).catch(() => {}); onChanged(); }} />

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">Status History</p>
            <div className="space-y-2">
              {order.status_history.map((h, i) => (
                <div key={`${h.status}-${h.at}-${i}`} className="flex items-start gap-2 text-sm">
                  <Clock className="mt-0.5 h-4 w-4 text-stone-400" />
                  <div><span className="font-medium text-stone-700">{h.status}</span> <span className="text-stone-400">· {h.by}</span>{h.note && <p className="text-xs text-stone-500">{h.note}</p>}</div>
                </div>
              ))}
            </div>
          </div>

          {/* VENDOR actions on a new request */}
          {isVendor && order.status === "New" && !modifying && !rejecting && !accepting && (
            <div className="flex flex-wrap gap-2 border-t border-[#F0ECE1] pt-4">
              {can("orders.approve") && <Button size="sm" disabled={busy} onClick={startAccept} className="bg-emerald-600 hover:bg-emerald-700" data-testid="order-accept-btn">Accept &amp; Set Prices</Button>}
              {can("orders.edit") && <Button size="sm" variant="outline" disabled={busy} onClick={startModify} className="border-[#D97706]/50 text-[#D97706]" data-testid="order-modify-btn">Modify Quantities</Button>}
              {can("orders.approve") && <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(true)} className="text-rose-600" data-testid="order-reject-btn">Reject</Button>}
            </div>
          )}

          {/* VENDOR accept + pricing */}
          {isVendor && accepting && (
            <div className="space-y-2 border-t border-[#F0ECE1] pt-4" data-testid="order-accept-form">
              <Label>Confirm unit prices (₹)</Label>
              <p className="text-xs text-stone-500">Pre-filled from your catalog. Set any missing price — the total calculates automatically.</p>
              {prices.map((p, i) => {
                const n = parseFloat(p.unit_price);
                const lt = n > 0 ? n * p.quantity : 0;
                return (
                  <div key={`${p.product_name}-${p.unit}`} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-stone-700">{p.product_name} <span className="text-xs text-stone-400">× {p.quantity} {p.unit}</span></span>
                    <span className="text-xs text-stone-400">₹</span>
                    <Input className="w-24" type="number" min="0" step="0.01" placeholder="Price" value={p.unit_price} onChange={(e) => setPrice(i, e.target.value)} data-testid={`accept-price-${i}`} />
                    <span className="w-24 text-right text-xs font-medium text-stone-600">₹{lt.toFixed(2)}</span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between rounded-lg bg-[#FAF8F5] px-3 py-2">
                <span className="text-sm font-semibold text-stone-700">Order Total</span>
                <span className="text-sm font-bold text-[#0D5C3A]" data-testid="accept-order-total">₹{prices.reduce((s, p) => { const n = parseFloat(p.unit_price); return s + (n > 0 ? n * p.quantity : 0); }, 0).toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={submitAccept} className="bg-emerald-600 hover:bg-emerald-700" data-testid="accept-submit-btn"><Check className="mr-1 h-4 w-4" /> Accept Order</Button>
                <Button size="sm" variant="outline" onClick={() => setAccepting(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* VENDOR modify form */}
          {isVendor && modifying && (
            <div className="space-y-2 border-t border-[#F0ECE1] pt-4" data-testid="order-modify-form">
              <Label>Adjust available quantities</Label>
              <p className="text-xs text-stone-500">Prices come from your catalog — line totals and the order total recalculate automatically.</p>
              <div className="hidden grid-cols-[1fr_72px_36px_84px_84px] gap-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400 sm:grid">
                <span>Product</span><span className="text-center">Qty</span><span></span><span className="text-right">Unit Price</span><span className="text-right">Line Total</span>
              </div>
              {mItems.map((it, i) => {
                const cp = catPrice(it.product_name, it.unit);
                return (
                  <div key={`${it.product_name}-${it.unit}`} className="grid grid-cols-[1fr_72px_36px_84px_84px] items-center gap-2">
                    <span className="text-sm text-stone-700">{it.product_name}</span>
                    <Input className="h-9 w-full text-center" type="number" min="0" value={it.quantity} onChange={(e) => setMItem(i, { quantity: e.target.value })} data-testid={`modify-qty-${i}`} />
                    <span className="text-xs text-stone-500">{it.unit}</span>
                    <span className="text-right text-sm text-stone-600" data-testid={`modify-unitprice-${i}`}>{cp === "" ? "—" : `₹${Number(cp).toFixed(2)}`}</span>
                    <span className="text-right text-sm font-medium text-stone-800" data-testid={`modify-linetotal-${i}`}>₹{mItemTotal(it).toFixed(2)}</span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between rounded-lg bg-[#FAF8F5] px-3 py-2">
                <span className="text-sm font-semibold text-stone-700">Modified Order Total</span>
                <span className="text-sm font-bold text-[#0D5C3A]" data-testid="modify-order-total">₹{mOrderTotal.toFixed(2)}</span>
              </div>
              {order.fulfillment_type !== "pickup" && (
                <label className="flex items-center gap-2 rounded-lg bg-[#FAF8F5] px-3 py-2 text-sm text-stone-700" data-testid="modify-pickup-toggle">
                  <input type="checkbox" checked={mFulfillment === "pickup"} onChange={(e) => setMFulfillment(e.target.checked ? "pickup" : "deliver")} />
                  Delivery unavailable — switch this order to Self-Pickup
                </label>
              )}
              <div><Label>Message to buyer *</Label><Textarea data-testid="modify-note" value={mNote} onChange={(e) => setMNote(e.target.value)} placeholder="e.g. Only 9 kg chicken available today" /></div>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={submitModify} className="bg-[#D97706] hover:bg-[#b45309]" data-testid="modify-submit-btn">Send to Buyer</Button>
                <Button size="sm" variant="outline" onClick={() => setModifying(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* VENDOR reject with reason */}
          {isVendor && rejecting && (
            <div className="space-y-2 border-t border-[#F0ECE1] pt-4" data-testid="order-reject-form">
              <Label>Reason for rejection (optional)</Label>
              <Textarea data-testid="reject-note" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => call("reject", { note: rejectNote })} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-submit-btn">Reject Order</Button>
                <Button size="sm" variant="outline" onClick={() => setRejecting(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* VENDOR progress */}
          {isVendor && order.status === "Accepted" && can("orders.approve", "logistics.edit") && (
            <div className="border-t border-[#F0ECE1] pt-4">
              <Button size="sm" disabled={busy} onClick={() => call("status", { status: "Delivered" })} className="bg-green-600 hover:bg-green-700" data-testid="order-deliver-btn"><Check className="mr-1 h-4 w-4" /> Mark Delivered</Button>
            </div>
          )}

          {/* RESTAURANT cancel new */}
          {!isVendor && order.status === "New" && can("procurement.edit") && (
            <div className="border-t border-[#F0ECE1] pt-4">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => call("status", { status: "Declined" })} className="text-rose-600" data-testid="order-cancel-btn"><X className="mr-1 h-4 w-4" /> Cancel Request</Button>
            </div>
          )}

          {/* RESTAURANT confirm after vendor delivered */}
          {!isVendor && order.status === "Missed Delivery" && !followUp && (
            <p className="flex items-center gap-1.5 border-t border-[#F0ECE1] pt-4 text-sm text-rose-700" data-testid="order-shortfall-reported"><AlertTriangle className="h-4 w-4" /> You reported a short delivery. You're billed only for what you received.</p>
          )}
          {!isVendor && order.status === "Delivered" && (order.buyer_confirmed || order.inventory_applied) && (
            <p className="flex items-center gap-1.5 border-t border-[#F0ECE1] pt-4 text-sm text-green-700" data-testid="order-delivery-confirmed"><Check className="h-4 w-4" /> You confirmed this delivery.</p>
          )}
          {!isVendor && order.status === "Delivered" && !(order.buyer_confirmed || order.inventory_applied) && !misOpen && !followUp && can("procurement.approve") && (
            <div className="space-y-2 border-t border-[#F0ECE1] pt-4">
              <p className="text-sm text-stone-600">The supplier marked this delivered. Please confirm:</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={busy} onClick={() => call("status", { status: "Delivered" })} className="bg-green-600 hover:bg-green-700" data-testid="order-confirm-delivered-btn"><Check className="mr-1 h-4 w-4" /> Delivered Correctly</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={startMis} className="border-rose-400 text-rose-600" data-testid="order-misdelivered-btn"><AlertTriangle className="mr-1 h-4 w-4" /> Missed Delivery</Button>
              </div>
            </div>
          )}
          {!isVendor && order.status === "Delivered" && misOpen && (
            <div className="space-y-2 border-t border-[#F0ECE1] pt-4" data-testid="misdelivered-form">
              <Label>Select short-delivered products and shortfall quantity</Label>
              {short.map((it, i) => (
                <div key={`${it.product_name}-${it.unit}`} className="flex items-center gap-2">
                  <input type="checkbox" checked={it.checked} onChange={(e) => setShort((p) => p.map((x, idx) => (idx === i ? { ...x, checked: e.target.checked } : x)))} data-testid={`mis-check-${i}`} />
                  <span className="flex-1 text-sm text-stone-700">{it.product_name} <span className="text-xs text-stone-400">(ordered {it.quantity} {it.unit})</span></span>
                  <Input className="w-24" type="number" placeholder="Short qty" value={it.less} disabled={!it.checked} onChange={(e) => setShort((p) => p.map((x, idx) => (idx === i ? { ...x, less: e.target.value } : x)))} data-testid={`mis-qty-${i}`} />
                </div>
              ))}
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={submitMis} className="bg-rose-600 hover:bg-rose-700" data-testid="mis-submit-btn">Report Mis-delivery</Button>
                <Button size="sm" variant="outline" onClick={() => setMisOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* RESTAURANT follow-up order prompt after mis-delivery */}
          {!isVendor && followUp && (
            <div className="space-y-3 border-t border-[#F0ECE1] pt-4" data-testid="followup-prompt">
              <div className="rounded-xl border border-[#0D5C3A]/30 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-[#0D5C3A]">Shortfall recorded</p>
                <p className="mt-1 text-sm text-stone-600">Create a follow-up order to <b>{order.to_name}</b> for the {followUp.length} short item(s)?</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={createFollowUp} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="followup-yes-btn"><Plus className="mr-1 h-4 w-4" /> Create Follow-up Order</Button>
                <Button size="sm" variant="outline" onClick={() => { setFollowUp(null); onClose(); }} data-testid="followup-no-btn">No thanks</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrderModal({ open, onOpenChange, onSaved, initial }) {
  const [vendors, setVendors] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [inv, setInv] = useState({});
  const [partnerId, setPartnerId] = useState("");
  const [win, setWin] = useState(null);
  const [rows, setRows] = useState([newRow()]);
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingItems, setPendingItems] = useState(null);
  const [tracked, setTracked] = useState({});
  const [fulfillment, setFulfillment] = useState("deliver");
  const [addrDefault, setAddrDefault] = useState("");
  const [addrAdditional, setAddrAdditional] = useState(null);
  const [addrSel, setAddrSel] = useState("default");
  const [addingAddr, setAddingAddr] = useState(false);
  const [newAddr, setNewAddr] = useState("");
  const [confirmDelAddr, setConfirmDelAddr] = useState(false);

  const deleteAddress = async () => {
    try {
      const r = await api.delete("/addresses");
      setAddrAdditional(r.data.additional || null);
      setAddrSel("default");
      setConfirmDelAddr(false);
      toast.success("Address deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveNewAddress = async () => {
    if (!newAddr.trim()) { toast.error("Enter an address"); return; }
    try {
      const r = await api.post("/addresses", { address: newAddr.trim() });
      setAddrAdditional(r.data.additional); setAddrSel("additional"); setAddingAddr(false); setNewAddr("");
      toast.success("Address saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const notifyRestock = async (name, unit) => {
    try {
      await api.post("/stock-interests", { vendor_id: partnerId, product_name: name, unit });
      setTracked((p) => ({ ...p, [`${name.trim().toLowerCase()}|${unit}`]: true }));
      toast.success("We'll notify you when it's back in stock");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const vr = await api.get("/partners");
      setVendors(vr.data.partners);
      const ir = await api.get("/inventory").catch(() => ({ data: { inventory: [] } }));
      const map = {};
      (ir.data.inventory || []).forEach((it) => { map[`${it.product_name.trim().toLowerCase()}|${it.unit}`] = it.quantity; });
      setInv(map);
      const ar = await api.get("/addresses").catch(() => ({ data: {} }));
      setAddrDefault(ar.data.default || ""); setAddrAdditional(ar.data.additional || null); setAddrSel(ar.data.selected || "default");
      setFulfillment("deliver"); setAddingAddr(false); setNewAddr("");
      setNotes(initial?.notes || ""); setDate(initial?.date || ""); setTime(initial?.time || "");
      setPartnerId(initial?.partner_business_id || "");
      setPendingItems(initial?.items || null);
      if (!initial) setRows([newRow()]);
    })();
  }, [open, initial]);

  // Load the SELECTED vendor's catalog (with prices) for automatic pricing
  useEffect(() => {
    if (!open || !partnerId) { setCatalog([]); return; }
    api.get(`/partners/${partnerId}/catalog`).then((r) => {
      setWin(r.data.order_window);
      const priced = r.data.products.filter((p) => p.price != null);
      setCatalog(priced);
      if (pendingItems) {
        setRows(pendingItems.map((it) => {
          const prod = priced.find((p) => p.name === it.product_name && p.unit === it.unit) || priced.find((p) => p.name === it.product_name);
          return { ...newRow(), product_id: prod?.id || "", product_name: it.product_name, unit: prod?.unit || it.unit, required: it.quantity };
        }));
        setPendingItems(null);
      } else {
        setRows([newRow()]);
      }
    }).catch(() => setCatalog([]));
  }, [open, partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
  const grouped = catalog.reduce((acc, p) => { (acc[p.category_name] = acc[p.category_name] || []).push(p); return acc; }, {});
  const prodOf = (pid) => catalog.find((x) => x.id === pid);
  const setRow = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const pickProduct = (i, pid) => { const prod = prodOf(pid); if (prod) setRow(i, { product_id: pid, product_name: prod.name, unit: prod.unit }); };
  const availableOf = (r) => { const p = prodOf(r.product_id); if (!p) return 0; return inv[`${p.name.trim().toLowerCase()}|${p.unit}`] || 0; };
  const toOrderQty = (r) => { const need = parseFloat(r.required); return need > 0 ? Math.max(0, +(need - availableOf(r)).toFixed(2)) : 0; };
  const lineTotal = (r) => { const p = prodOf(r.product_id); return p && p.price != null ? p.price * toOrderQty(r) : 0; };
  const orderTotal = rows.reduce((s, r) => s + lineTotal(r), 0);

  const save = async () => {
    if (!partnerId) { toast.error("Select a supplier first"); return; }
    if (rows.some((r) => r.product_id && prodOf(r.product_id)?.in_stock === false)) { toast.error("Remove out-of-stock items before ordering"); return; }
    const valid = rows.filter((r) => r.product_id && toOrderQty(r) > 0);
    if (valid.length === 0) { toast.error("Enter a required quantity greater than your current stock"); return; }
    if (!date) { toast.error(`Requested ${fulfillment === "pickup" ? "pickup" : "delivery"} date is required`); return; }
    if (!time) { toast.error(`Requested ${fulfillment === "pickup" ? "pickup" : "delivery"} time is required`); return; }
    const deliveryAddress = fulfillment === "deliver" ? (addrSel === "additional" ? addrAdditional : addrDefault) : null;
    if (fulfillment === "deliver" && !deliveryAddress) { toast.error("A delivery address is required"); return; }
    setBusy(true);
    try {
      await api.post("/orders", {
        partner_business_id: partnerId,
        items: valid.map((r) => ({ product_name: r.product_name, quantity: toOrderQty(r), unit: r.unit })),
        notes: notes || null, requested_delivery_date: date, requested_delivery_time: time,
        parent_order_id: initial?.parent_order_id || null,
        fulfillment_type: fulfillment, delivery_address: deliveryAddress,
      });
      toast.success("Supply request sent"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rv-scroll" data-testid="create-order-modal">
        <DialogHeader><DialogTitle>New Supply Request</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Supplier</Label>
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger data-testid="order-vendor-select" className="mt-1"><SelectValue placeholder="Select a connected supplier" /></SelectTrigger>
              <SelectContent>
                {vendors.length === 0 ? <div className="px-3 py-2 text-sm text-stone-400">No suppliers connected. Add one in Partners.</div> :
                  vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.profile_code})</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-stone-400">Prices are fetched automatically from the selected supplier's catalog.</p>
            {partnerId && win?.enabled && !win.is_open && (
              <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800" data-testid="order-window-closed-notice">
                This supplier accepts orders only between {win.open} and {win.close} ({win.timezone}). Ordering reopens {new Date(win.next_change).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: win.timezone })} ({win.timezone}).
              </p>
            )}
            {partnerId && win?.enabled && win.is_open && <p className="mt-2 text-xs font-medium text-emerald-700" data-testid="order-window-open-notice">Ordering open until {win.close} ({win.timezone}).</p>}
          </div>

          {!partnerId ? (
            <p className="rounded-lg bg-[#F0ECE1] px-3 py-2 text-sm text-stone-600">Select a supplier to load their priced products.</p>
          ) : catalog.length === 0 ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">This supplier hasn't added any priced products yet.</p>
          ) : (
            <div>
              <div className="mb-1 flex items-center justify-between"><Label>Products</Label><span className="text-xs text-stone-400">Enter total needed — we subtract your stock</span></div>
              <div className="hidden grid-cols-[1fr_72px_64px_64px_76px_24px] gap-2 px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400 sm:grid">
                <span>Product</span><span className="text-center">In stock</span><span className="text-center">Need</span><span className="text-center">To order</span><span className="text-right">Total</span><span></span>
              </div>
              <div className="space-y-2">
                {rows.map((r, i) => {
                  const oos = prodOf(r.product_id)?.in_stock === false;
                  return (
                    <div key={r._key} className="grid grid-cols-[1fr_72px_64px_64px_76px_24px] items-center gap-2">
                      <div>
                        <Select value={r.product_id} onValueChange={(v) => pickProduct(i, v)}>
                          <SelectTrigger className="w-full" data-testid={`order-item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(grouped).map(([cat, prods]) => (
                              <SelectGroup key={cat}>
                                <SelectLabel>{cat}</SelectLabel>
                                {prods.map((p) => <SelectItem key={p.id} value={p.id} disabled={p.in_stock === false}>{p.name} — ₹{Number(p.price).toFixed(2)}/{p.unit}{p.in_stock === false ? " · Out of Stock" : ""}</SelectItem>)}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        {oos && (
                          <div className="mt-0.5 flex items-center gap-2">
                            <span className="inline-block rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600" data-testid={`order-item-oos-${i}`}>Out of Stock</span>
                            {tracked[`${r.product_name.trim().toLowerCase()}|${r.unit}`] ? (
                              <span className="text-[10px] font-medium text-emerald-600" data-testid={`order-item-tracked-${i}`}>We'll notify you</span>
                            ) : (
                              <button type="button" onClick={() => notifyRestock(r.product_name, r.unit)} className="text-[10px] font-semibold text-[#0D5C3A] underline" data-testid={`order-item-notify-${i}`}>Notify me when back</button>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-center text-xs text-stone-500" data-testid={`order-item-avail-${i}`}>{r.product_id ? `${availableOf(r)} ${r.unit}` : "—"}</span>
                      <Input data-testid={`order-item-need-${i}`} className="h-9 text-center" type="number" min="0" placeholder="0" value={r.required || ""} onChange={(e) => setRow(i, { required: e.target.value })} />
                      <span className="text-center text-sm font-semibold text-[#0D5C3A]" data-testid={`order-item-toorder-${i}`}>{toOrderQty(r)}</span>
                      <span className="text-right text-sm font-medium text-stone-700" data-testid={`order-item-total-${i}`}>{money(lineTotal(r))}</span>
                      {rows.length > 1 ? <button onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} className="text-rose-500" data-testid={`order-item-remove-${i}`}><Trash2 className="h-4 w-4" /></button> : <span />}
                    </div>
                  );
                })}
              </div>
              <Button variant="ghost" size="sm" className="mt-2 text-[#0D5C3A]" onClick={() => setRows((p) => [...p, newRow()])} data-testid="order-add-item-btn"><Plus className="mr-1 h-4 w-4" /> Add item</Button>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-[#FAF8F5] px-3 py-2">
                <span className="text-sm font-semibold text-stone-700">Order Total</span>
                <span className="font-heading text-lg font-bold text-[#0D5C3A]" data-testid="order-cart-total">{money(orderTotal)}</span>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[#E5E0D8] p-3">
            <Label>Order Fulfillment</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setFulfillment("deliver")} data-testid="fulfillment-deliver-btn"
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${fulfillment === "deliver" ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-white text-stone-600"}`}>Deliver</button>
              <button type="button" onClick={() => setFulfillment("pickup")} data-testid="fulfillment-pickup-btn"
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${fulfillment === "pickup" ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-white text-stone-600"}`}>Self-Pickup</button>
            </div>
            {fulfillment === "deliver" && (
              <div className="mt-3 space-y-2" data-testid="delivery-address-block">
                <Label className="text-xs text-stone-500">Delivery Address</Label>
                <button type="button" onClick={() => setAddrSel("default")} data-testid="addr-default-btn"
                  className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm ${addrSel === "default" ? "border-[#0D5C3A] bg-emerald-50" : "border-[#E5E0D8]"}`}>
                  <span className={`mt-0.5 h-3.5 w-3.5 rounded-full border ${addrSel === "default" ? "border-[#0D5C3A] bg-[#0D5C3A]" : "border-stone-300"}`} />
                  <span className="flex-1"><b className="text-xs text-stone-400">Registered (default)</b><br />{addrDefault || "—"}</span>
                </button>
                {addrAdditional && (
                  <div className={`rounded-lg border px-3 py-2 text-sm ${addrSel === "additional" ? "border-[#0D5C3A] bg-emerald-50" : "border-[#E5E0D8]"}`}>
                    <div className="flex items-start gap-2">
                      <button type="button" onClick={() => setAddrSel("additional")} data-testid="addr-additional-btn" className="flex flex-1 items-start gap-2 text-left">
                        <span className={`mt-0.5 h-3.5 w-3.5 rounded-full border ${addrSel === "additional" ? "border-[#0D5C3A] bg-[#0D5C3A]" : "border-stone-300"}`} />
                        <span className="flex-1"><b className="text-xs text-stone-400">Additional</b><br />{addrAdditional}</span>
                      </button>
                      <button type="button" onClick={() => setConfirmDelAddr(true)} data-testid="addr-delete-btn" className="shrink-0 text-rose-500 hover:text-rose-600" title="Delete this address"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                )}
                {!addrAdditional && !addingAddr && (
                  <Button type="button" variant="ghost" size="sm" className="text-[#0D5C3A]" onClick={() => setAddingAddr(true)} data-testid="add-address-btn"><Plus className="mr-1 h-4 w-4" /> Add New Address</Button>
                )}
                {addingAddr && (
                  <div className="space-y-2">
                    <Textarea data-testid="new-address-input" placeholder="Enter the new delivery address" value={newAddr} onChange={(e) => setNewAddr(e.target.value)} />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" className="bg-[#0D5C3A] hover:bg-[#084229]" onClick={saveNewAddress} data-testid="save-address-btn">Save Address</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => { setAddingAddr(false); setNewAddr(""); }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <AlertDialog open={confirmDelAddr} onOpenChange={setConfirmDelAddr}>
              <AlertDialogContent data-testid="addr-delete-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure you want to delete this address?</AlertDialogTitle>
                  <AlertDialogDescription>Your registered business address stays as the default. You can add a new additional address afterwards.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="addr-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAddress} data-testid="addr-delete-confirm-btn" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {fulfillment === "pickup" && <p className="mt-2 text-xs text-stone-500" data-testid="pickup-note">You'll collect this order from the supplier. Set your preferred pickup date and time below.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{fulfillment === "pickup" ? "Pickup Date *" : "Delivery Date *"}</Label><Input data-testid="order-delivery-date" type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label>{fulfillment === "pickup" ? "Pickup Time *" : "Delivery Time *"}</Label><Input data-testid="order-delivery-time" type="time" className="mt-1" value={time} onChange={(e) => setTime(e.target.value)} /></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea data-testid="order-notes-input" className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="create-order-submit-btn" disabled={busy || !partnerId || orderTotal <= 0} onClick={save} className="bg-[#0D5C3A] hover:bg-[#084229]">Send Request ({money(orderTotal)})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
